import { useState, useEffect } from 'react';
import { Sliders, HelpCircle, RotateCcw, Save, Download } from 'lucide-react';
import { CHAIN_LIMITS, calculateRequiredWallets } from '../../utils/bundleCalc';
import { formatNumber, formatTokenPrice, formatCurrency, formatCurrencyCompact } from '../../utils/bundleFormatters';
import { BundleTooltip } from './BundleTooltip';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const CHAIN_ORDER = [
  'ETH', 'SOL', 'BNB', 'BASE', 'KROWN', 'ROBINHOOD',
  'ARBITRUM', 'OPTIMISM', 'POLYGON', 'AVALANCHE',
  'LINEA', 'SCROLL', 'BLAST', 'ZKSYNC', 'MANTLE',
];

export default function InputSection({
  inputs,
  dexes = [],
  onInputChange,
  onChainChange,
  onDexChange,
  onBaseCurrencyChange,
  onLoadPool,
  poolLoading,
  onReset,
  onSave,
  onLoad,
  results,
}) {
  const [supplyText, setSupplyText] = useState(formatNumber(inputs.totalSupply));
  const [poolAddr, setPoolAddr] = useState('');
  useEffect(() => { setSupplyText(formatNumber(inputs.totalSupply)); }, [inputs.totalSupply]);

  const chainMeta = CHAIN_LIMITS[inputs.chain] || CHAIN_LIMITS.ETH;
  const chainSymbol = inputs.baseCurrency === 'USDG' ? 'USDG' : chainMeta.nativeSymbol;

  const handleSupplyChange = (raw) => {
    const cleaned = raw.replace(/,/g, '');
    if (cleaned === '' || !Number.isNaN(Number(cleaned))) {
      setSupplyText(cleaned === '' ? '' : formatNumber(Number(cleaned)));
      onInputChange('totalSupply', cleaned || 0);
    }
  };

  const requiredWallets = calculateRequiredWallets(inputs.totalSupply, inputs.bundlePercentage, inputs.maxWalletPercent);
  const effectiveWalletCount = inputs.maxWalletPercent > 0 ? requiredWallets : inputs.walletCount;
  const overChainLimit = effectiveWalletCount > chainMeta.maxWallets;

  const currentDex = dexes.find(d => d.id === inputs.dex) || dexes[0];
  const feeTiers = currentDex?.fee_tiers || [inputs.dexFee || 0.30];

  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base sm:text-lg font-semibold flex items-center" data-testid="bundle-inputs-title">
          <Sliders className="w-4 h-4 mr-2 text-primary" />
          Inputs
        </h2>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onSave} data-testid="bundle-save-btn" title="Save preset">
            <Save className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onLoad} data-testid="bundle-load-btn" title="Load preset">
            <Download className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onReset} data-testid="bundle-reset-btn" title="Reset">
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Chain picker */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-muted-foreground mb-2">Blockchain</label>
        <div className="grid grid-cols-3 gap-2">
          {CHAIN_ORDER.map(id => (
            <button
              key={id}
              onClick={() => onChainChange(id)}
              data-testid={`bundle-chain-${id.toLowerCase()}-btn`}
              className={`px-2 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                inputs.chain === id
                  ? 'bg-primary/20 text-primary border border-primary/40'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70 border border-transparent'
              }`}
            >
              {CHAIN_LIMITS[id].name}
            </button>
          ))}
        </div>
      </div>

      {/* Base currency toggle — Robinhood pools are usually USDG-denominated */}
      {inputs.chain === 'ROBINHOOD' && (
        <div className="mb-4">
          <label className="block text-xs font-medium text-muted-foreground mb-2">LP Denominated In</label>
          <div className="grid grid-cols-2 gap-2">
            {[['NATIVE', 'ETH'], ['USDG', 'USDG ($1)']].map(([val, label]) => (
              <button
                key={val}
                onClick={() => onBaseCurrencyChange && onBaseCurrencyChange(val)}
                data-testid={`bundle-base-${val.toLowerCase()}-btn`}
                className={`px-2 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  (inputs.baseCurrency || 'NATIVE') === val
                    ? 'bg-primary/20 text-primary border border-primary/40'
                    : 'bg-muted text-muted-foreground hover:bg-muted/70 border border-transparent'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {inputs.baseCurrency === 'USDG' && (
            <p className="text-[10px] text-muted-foreground/70 mt-1">USDG is Robinhood Chain's native stablecoin — price fixed at $1.</p>
          )}
        </div>
      )}

      {/* DEX picker */}
      {dexes.length > 0 && (
        <div className="mb-4">
          <label className="block text-xs font-medium text-muted-foreground mb-2">DEX / Pool Type</label>
          <select
            value={inputs.dex || ''}
            onChange={(e) => onDexChange(e.target.value)}
            data-testid="bundle-dex-select"
            className="w-full px-3 py-2 bg-muted rounded-lg text-sm border border-border focus:border-primary focus:outline-none text-foreground"
          >
            {dexes.map(d => (
              <option key={d.id} value={d.id}>{d.name} — {d.default_fee}% base fee</option>
            ))}
          </select>
          {currentDex?.notes && (
            <p className="text-[10px] text-muted-foreground/70 mt-1 leading-relaxed">{currentDex.notes}</p>
          )}
        </div>
      )}

      {/* Live pool loader */}
      {onLoadPool && (
        <div className="mb-4">
          <label className="block text-xs font-medium text-muted-foreground mb-2">Read Live Pool (optional)</label>
          <div className="flex gap-2">
            <Input
              type="text"
              value={poolAddr}
              onChange={(e) => setPoolAddr(e.target.value)}
              placeholder="Pool address / v4 poolId"
              className="font-mono text-xs"
              data-testid="bundle-pool-input"
            />
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              onClick={() => onLoadPool(poolAddr)}
              disabled={!poolAddr.trim() || poolLoading}
              data-testid="bundle-pool-load-btn"
            >
              {poolLoading ? '…' : 'Read'}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground/70 mt-1 leading-relaxed">
            Reads live reserves via RPC (v2/v3) or DexScreener (v4/Solana) and prefills
            Supply (tokens in pool), Liquidity &amp; base price.
          </p>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <div className="flex items-center mb-1.5">
            <label className="text-xs font-medium text-muted-foreground">Total Token Supply</label>
            <BundleTooltip content="The total number of tokens minted for your launch">
              <HelpCircle className="ml-1 text-muted-foreground/60 w-3 h-3" />
            </BundleTooltip>
          </div>
          <Input
            type="text"
            value={supplyText}
            onChange={(e) => handleSupplyChange(e.target.value)}
            className="font-mono"
            data-testid="bundle-supply-input"
            placeholder="1,000,000,000"
          />
        </div>

        <SliderRow
          label={`% Supply to Bundle (Target Hold)`}
          tooltip="Percentage of total supply you want to hold after bundling"
          min={1} max={99} step={0.5}
          value={inputs.bundlePercentage}
          onChange={(v) => onInputChange('bundlePercentage', v)}
          suffix="%"
          testid="bundle-pct-slider"
          disabled={inputs.maxWalletPercent > 0 && overChainLimit}
          warn={inputs.maxWalletPercent > 0 && overChainLimit
            ? '⚠ Auto-capped by wallet limit. Lower Max Wallet % or Bundle % to override.'
            : null}
        />

        <div>
          <div className="flex items-center mb-1.5">
            <label className="text-xs font-medium text-muted-foreground">Initial Liquidity ({chainSymbol})</label>
            <BundleTooltip content={`Amount of ${chainSymbol} added as initial LP (B₀)`}>
              <HelpCircle className="ml-1 text-muted-foreground/60 w-3 h-3" />
            </BundleTooltip>
          </div>
          <Input
            type="number"
            value={inputs.initialLiquidity}
            onChange={(e) => onInputChange('initialLiquidity', e.target.value)}
            className="font-mono"
            step="0.1" min="0"
            data-testid="bundle-liquidity-input"
          />
        </div>

        <div>
          <div className="flex items-center mb-1.5">
            <label className="text-xs font-medium text-muted-foreground">{chainSymbol} Price (USD)</label>
            <BundleTooltip content={`Current price of ${chainSymbol} in USD`}>
              <HelpCircle className="ml-1 text-muted-foreground/60 w-3 h-3" />
            </BundleTooltip>
          </div>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
            <Input
              type="number"
              value={inputs.baseCurrencyPrice}
              onChange={(e) => onInputChange('baseCurrencyPrice', e.target.value)}
              className="pl-7 font-mono"
              data-testid="bundle-baseprice-input"
            />
          </div>
        </div>

        <SliderRow
          label="Token Tax (%)"
          tooltip="Buy tax charged by the token contract itself (0-99%)"
          min={0} max={20} step={0.5}
          value={inputs.taxRate}
          onChange={(v) => onInputChange('taxRate', v)}
          suffix="%"
          testid="bundle-tax-slider"
        />

        <div>
          <div className="flex items-center mb-1.5">
            <label className="text-xs font-medium text-muted-foreground">DEX Swap Fee (%)</label>
            <BundleTooltip content={`Pool swap fee tier. ${currentDex?.notes || ''}`}>
              <HelpCircle className="ml-1 text-muted-foreground/60 w-3 h-3" />
            </BundleTooltip>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {feeTiers.map(t => (
              <button
                key={t}
                onClick={() => onInputChange('dexFee', t)}
                data-testid={`bundle-fee-${String(t).replace('.', '_')}-btn`}
                className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                  Number(inputs.dexFee) === Number(t)
                    ? 'bg-primary/20 text-primary border-primary/50'
                    : 'bg-muted border-border text-muted-foreground hover:bg-muted/70'
                }`}
              >
                {t}%
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center mb-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              {inputs.maxWalletPercent > 0 ? 'Wallets (Auto)' : 'Number of Wallets'}
            </label>
            <BundleTooltip content={inputs.maxWalletPercent > 0
              ? `Auto-calculated from max wallet cap. ${effectiveWalletCount} wallets needed.`
              : `Max ${chainMeta.maxWallets} wallets per block for ${chainMeta.name}`}>
              <HelpCircle className="ml-1 text-muted-foreground/60 w-3 h-3" />
            </BundleTooltip>
          </div>
          {inputs.maxWalletPercent === 0 ? (
            <>
              <input
                type="range"
                min="1"
                max={chainMeta.maxWallets}
                step="1"
                value={Math.min(inputs.walletCount, chainMeta.maxWallets)}
                onChange={(e) => onInputChange('walletCount', e.target.value)}
                className="w-full accent-primary"
                data-testid="bundle-walletcount-slider"
              />
              <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
                <span>1</span>
                <span className="font-mono text-foreground">{Math.min(inputs.walletCount, chainMeta.maxWallets)}</span>
                <span>{chainMeta.maxWallets}</span>
              </div>
            </>
          ) : (
            <div className={`p-2.5 rounded-lg border ${overChainLimit ? 'bg-destructive/10 border-destructive/40' : 'bg-primary/5 border-primary/30'}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Required wallets:</span>
                <span className={`text-base font-bold font-mono ${overChainLimit ? 'text-destructive' : 'text-primary'}`}>
                  {effectiveWalletCount}
                </span>
              </div>
              <p className={`text-[10px] mt-1 ${overChainLimit ? 'text-destructive' : 'text-emerald-500'}`}>
                {overChainLimit
                  ? `⚠ Exceeds ${chainMeta.name} limit (${chainMeta.maxWallets})`
                  : `✓ Within ${chainMeta.name} limit (${chainMeta.maxWallets})`}
              </p>
            </div>
          )}
        </div>

        <SliderRow
          label="Max Wallet Cap (%)"
          tooltip="Maximum % of supply any single wallet can hold. Set to 0 to disable."
          min={0} max={5} step={0.1}
          value={inputs.maxWalletPercent}
          onChange={(v) => onInputChange('maxWalletPercent', v)}
          suffix={inputs.maxWalletPercent === 0 ? ' (off)' : '%'}
          testid="bundle-maxwallet-slider"
          hint={inputs.maxWalletPercent > 0
            ? `ℹ Wallet count auto-set to ${effectiveWalletCount}`
            : null}
        />
      </div>

      {results && results.initialTokenPriceUsd !== undefined && (
        <div className="mt-5 p-3 rounded-lg bg-muted/50 border border-border text-xs space-y-1">
          <div className="flex justify-between"><span className="text-muted-foreground">Initial price:</span> <span className="font-mono">{formatCurrencyCompact(results.initialTokenPriceUsd)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Final price:</span> <span className="font-mono text-emerald-500">{formatCurrencyCompact(results.finalTokenPriceUsd)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Δ price:</span> <span className="font-mono text-emerald-500">{formatNumber(results.priceIncreaseMultiple, 2)}×</span></div>
          <div className="flex justify-between border-t border-border pt-1 mt-1"><span className="text-muted-foreground">Bundle cost:</span> <span className="font-mono">{formatCurrency(results.bundleCostUsd)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Effective fee load:</span> <span className="font-mono">{formatNumber(results.effectiveFeePct, 2)}%</span></div>
        </div>
      )}
    </div>
  );
}

// --- helpers ---
function SliderRow({ label, tooltip, min, max, step, value, onChange, suffix, testid, disabled, warn, hint }) {
  return (
    <div>
      <div className="flex items-center mb-1.5">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        {tooltip && (
          <BundleTooltip content={tooltip}>
            <HelpCircle className="ml-1 text-muted-foreground/60 w-3 h-3" />
          </BundleTooltip>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={min} max={max} step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full accent-primary"
          disabled={disabled}
          data-testid={testid}
        />
        <span className="min-w-[56px] text-center bg-muted px-2 py-1 rounded text-xs font-mono">
          {typeof value === 'number' ? value : Number(value) || 0}{suffix}
        </span>
      </div>
      {warn && <p className="text-[11px] text-amber-500 mt-1">{warn}</p>}
      {hint && <p className="text-[11px] text-primary/80 mt-1">{hint}</p>}
    </div>
  );
}
